// Small pieces of the networks list, New network and the network tabs (feature spec 17, phase 1).
import { CircleX, Info, TriangleAlert } from 'lucide-react';
import type { Diagnostic } from '../../api/types';
import { strings } from '../../strings';
import { providerLabel } from '../shared';

const TILE: Record<string, string> = { azure: 'Az', aws: 'AWS', gcp: 'GC', cloudflare: 'CF' };

/** A provider's tile: a short text (never color alone); hidden from assistive tech, the chip names the provider. */
export function ProviderTile({ provider }: { provider: string }) {
  return (
    <span aria-hidden className={`net-provider-tile net-provider-${provider}`}>
      {TILE[provider] ?? provider.slice(0, 3).toUpperCase()}
    </span>
  );
}

/** "Az westeurope": a region with its provider's tile (the networks list, mock n01). */
export function RegionChip({ provider, region }: { provider: string; region: string }) {
  return (
    <span className="net-chip">
      <ProviderTile provider={provider} />
      <span className="sr-only">{providerLabel(provider)} </span>
      {region}
    </span>
  );
}

/** A diagnostic in its tone, with its message and its code in monospace; `info` is neutral and blocks nothing. */
export function NetworkDiagnostic({ diagnostic }: { diagnostic: Diagnostic }) {
  const Icon =
    diagnostic.severity === 'error' ? CircleX : diagnostic.severity === 'warning' ? TriangleAlert : Info;
  const tone =
    diagnostic.severity === 'error'
      ? 'net-diag-error'
      : diagnostic.severity === 'warning'
        ? 'net-diag-warning'
        : 'net-diag-info';
  return (
    <span className={`net-diag ${tone}`}>
      <Icon aria-hidden className="size-4 shrink-0" />
      <span className="sr-only">{strings.status.severity[diagnostic.severity]}:</span>
      <span className="min-w-0">
        {diagnostic.message} <code className="font-mono text-xs">{diagnostic.code}</code>
      </span>
    </span>
  );
}

export function NetworkDiagnosticList({ diagnostics }: { diagnostics: readonly Diagnostic[] }) {
  if (!diagnostics.length) return null;
  return (
    <ul className="space-y-1">
      {diagnostics.map((d, i) => (
        <li key={`${d.code}-${d.path}-${i}`}>
          <NetworkDiagnostic diagnostic={d} />
        </li>
      ))}
    </ul>
  );
}
